<script lang="ts">
import { Component, Watch } from "vue-property-decorator";
import { useAppEditorStore } from "@/stores/appEditor";
import { NodeData } from "@/types/data";
import { EventBus } from "@/eventbus";
import LineGraphComponent from "./LineGraphComponent.vue";
import { LineGraphOptions } from "./LineGraphOptions";

// NOTE: This and StackedGraphWrapper should both incorporate fix from https://github.com/ScreenfeedDigitalSignage/connect/pull/220/files

@Component
export default class LineGraphWrapper extends LineGraphComponent {
  get appEditor() {
    return useAppEditorStore();
  }

  get model() {
    return this.appEditor.widgetById(this.wid) as LineGraphOptions;
  }

  get widgetData() {
    return this.appEditor.widgetData;
  }

  apply(props: any) {
    this.appEditor.setWidgetProps([this.wid], props, "NO_UNDO");
  }

  setDataDependentProps() {
    this.setAxisColumns();
    this.$nextTick(() => {
      this.setInitial();
    });
  }

  created() {
    EventBus.on("UNDO_FIRED", this.setDataDependentProps);
  }

  beforeDestroy() {
    EventBus.off("UNDO_FIRED", this.setDataDependentProps);
  }

  @Watch("dataWatchProp", { immediate: true })
  onDataBindingChanged() {
    this.setDataDependentProps();
  }

  get initialXTitle() {
    if (this.xColumnOptions.length > 0) {
      return this.dataSet[0]?.find(
        (c) => c.uuid === this.xColumnOptions[0].value
      )?.displayName;
    } else {
      return "";
    }
  }

  get initialYTitle() {
    if (this.yColumnOptions.length > 0) {
      return this.dataSet[0]?.find(
        (c) => c.uuid === this.yColumnOptions[0].value
      )?.displayName;
    } else {
      return "";
    }
  }

  setInitial() {
    const payload: Partial<LineGraphOptions> = {
      xTitle: this.initialXTitle,
      yTitle: this.initialYTitle,
    };
    if (!isNaN(this.NewMaxValue)) {
      payload.maxValue = this.NewMaxValue;
    }
    if (!isNaN(this.NewMinValue)) {
      payload.minValue = this.NewMinValue;
    }
    this.apply(payload);
  }

  get dataWatchProp() {
    const id = this.dataBinding?.dataConnectionUuid;
    const ph = this.widgetData[this.wid]?.[0]?._placeholderData?.toString();
    return `c${id}-${ph}`;
  }

  get dataBinding() {
    const bindings = this.appEditor.bindingsForComponent({
      widgetId: this.wid,
      bindingType: "DataSet",
    });
    return bindings.length > 0 ? bindings[0] : undefined;
  }

  get dataSet(): NodeData[][] {
    const dataSet =
      (this.widgetData[this.wid]?.[0]?.data as NodeData[][]) ?? [];
    return dataSet;
  }

  get dataColumns(): NodeData[] {
    return this.dataSet.length > 0 ? this.dataSet[0] : [];
  }

  get trimmedDataSet(): NodeData[][] {
    let data = this.dataSet;
    const isNullRow = (row: NodeData[]) => {
      const raw = row?.find((c) => c.uuid === this.yAxisColumnId)?.value;
      if (raw === null || raw === undefined || raw === "") return true;
      return isNaN(parseInt(raw as string));
    };
    while (data.length > 0 && isNullRow(data[0])) data = data.slice(1);
    while (data.length > 0 && isNullRow(data[data.length - 1]))
      data = data.slice(0, data.length - 1);
    return data;
  }

  get labels(): string[] {
    return this.trimmedDataSet.map(
      (row) =>
        row.find((c) => c.uuid === this.xAxisColumnId)?.formattedValue as string
    );
  }

  get values(): (number | null)[] {
    return this.trimmedDataSet.map((row) => {
      const raw = row?.find((c) => c.uuid === this.yAxisColumnId)?.value;
      if (raw === null || raw === undefined || raw === "") return null;
      const parsed = parseInt(raw as string);
      return isNaN(parsed) ? null : parsed;
    });
  }

  get NewMinValue() {
    let smallestValue = Math.floor(
      Math.min(...(this.values.filter((v) => v !== null) as number[]))
    );
    return smallestValue < 0 ? smallestValue : 0;
  }

  get NewMaxValue() {
    return Math.ceil(
      Math.max(...(this.values.filter((v) => v !== null) as number[]))
    );
  }

  get xColumnOptions() {
    return this.dataColumns
      .filter(
        (c) =>
          c.dataType === "String" ||
          c.dataType === "Date" ||
          c.dataType === "Time" ||
          c.dataType === "DateTime"
      )
      .map((c) => {
        return {
          label: c.displayName,
          value: c.uuid,
        };
      });
  }

  get yColumnOptions() {
    return this.dataColumns
      .filter((c) => c.dataType === "Number" && c.query != "__sf_index__")
      .map((c) => {
        return {
          label: c.displayName,
          value: c.uuid,
        };
      });
  }

  setAxisColumns() {
    const xAxisColumnIdOptions = this.xColumnOptions.map((o) => o.value);
    const yAxisColumnIdOptions = this.yColumnOptions.map((o) => o.value);
    if (
      xAxisColumnIdOptions &&
      xAxisColumnIdOptions.length > 0 &&
      !xAxisColumnIdOptions.includes(this.xAxisColumnId)
    ) {
      let xAxisColumnId = xAxisColumnIdOptions[0];
      this.apply({ xAxisColumnId });
    }
    if (
      yAxisColumnIdOptions &&
      yAxisColumnIdOptions.length > 0 &&
      !yAxisColumnIdOptions.includes(this.yAxisColumnId)
    ) {
      let yAxisColumnId = yAxisColumnIdOptions[0];
      this.apply({ yAxisColumnId });
    }
  }
}
</script>
